import React, { useState, useCallback, useMemo } from "react";
import { Button, App, Flex, Typography } from "antd";
import { Copy, Check, Play, X } from "lucide-react";
import { getVideoShareLink } from "../services/videoUploader";
import { enhancedCopyToClipboard } from "../utils/clipboard";

const { Title } = Typography;
interface UploadCompleteNotificationProps {
  videoId: string;
  videoGuid: string;
  onClose?: () => void;
}

const UploadCompleteNotification: React.FC<UploadCompleteNotificationProps> = ({
  videoId,
  videoGuid,
  onClose,
}) => {
  const { notification } = App.useApp();
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isError, setIsError] = useState(false);

  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  // Convert videoId without hyphens back to proper UUID format for watch URL
  const videoIdWithoutHyphens = videoId.replace(/-/g, "");

  const watchUrl = `${baseUrl}/video/${videoIdWithoutHyphens}`;

  const handleCopyLink = useCallback(async () => {
    try {
      let linkToCopy = shareLink;

      // If no cached share link, fetch a new one
      if (!linkToCopy) {
        setIsLoading(true);
        const response = await getVideoShareLink({ videoId }); // videoId is already without hyphens
        if (response.link) {
          linkToCopy = response.link;
          setShareLink(linkToCopy);
        } else {
          throw new Error("No link received from API");
        }
      }

      // Ensure linkToCopy is a string
      if (!linkToCopy) {
        throw new Error("No link available to copy");
      }

      // Use enhanced clipboard utility with Safari fallback
      const success = await enhancedCopyToClipboard(
        linkToCopy,
        () => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 1000);
        },
        (error) => {
          console.error("Failed to copy share link:", error);
          setIsError(true);
          setTimeout(() => setIsError(false), 1000);
        }
      );

      if (!success) {
        setIsError(true);
        setTimeout(() => setIsError(false), 1000);
      }
    } catch (error) {
      console.error("Failed to copy share link:", error);
      setIsError(true);
      setTimeout(() => setIsError(false), 1000);
    } finally {
      setIsLoading(false);
    }
  }, [shareLink, videoId]);

  const handleWatchVideo = useCallback(() => {
    window.open(watchUrl, "_blank");
  }, [watchUrl]);

  const handleClose = useCallback(() => {
    setShareLink(null);
    setIsCopied(false);
    setIsLoading(false);
    setIsError(false);
    onClose?.();
  }, [onClose]);

  const actions = useMemo(
    () => [
      <Flex key="actions" gap={8} align="center">
        <Button
          variant="outlined"
          loading={isLoading}
          onClick={handleCopyLink}
          disabled={isLoading}
          style={{
            color: "#4A3CFA",
            borderColor: "#4A3CFA",
            fontFamily:
              "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            fontSize: "14px",
            fontWeight: 500,
            padding: "4px 8px",
          }}
        >
          <Flex gap={4} align="center">
            {isError ? (
              <X size={14} />
            ) : isCopied ? (
              <Check size={14} />
            ) : (
              <Copy size={14} />
            )}
            {isError
              ? "Oops—try again!"
              : isLoading
              ? "Copying…"
              : isCopied
              ? "Link Copied!"
              : "Copy Share Link"}
          </Flex>
        </Button>
        <Button
          type="primary"
          onClick={handleWatchVideo}
          style={{
            backgroundColor: "#4A3CFA",
            borderColor: "#4A3CFA",
            fontFamily:
              "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            fontSize: "14px",
            fontWeight: 500,
            borderRadius: "6px",
            padding: "4px 12px",
          }}
        >
          <Flex gap={4} align="center">
            <Play size={14} />
            Watch on Dadan
          </Flex>
        </Button>
      </Flex>,
    ],
    [handleCopyLink, handleWatchVideo, isCopied, isLoading, isError]
  );

  const openNotification = useCallback(() => {
    const key = `upload-done-${videoId}`;

    notification.open({
      message: (
        <Title level={5} style={{ margin: 0 }}>
          Your recording is ready 🎉
        </Title>
      ),
      description:
        "We've finished processing your video recording. It's uploaded to Dadan and ready to share.",
      actions,
      key,
      onClose: handleClose,
      duration: 0, // Don't auto-close
      style: {
        borderRadius: "6px",
        fontFamily:
          "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        border: "1px solid #f0f0f0",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      },
      className: "custom-upload-notification",
    });
  }, [videoId, actions, handleClose, notification]);

  // Auto-open notification when component mounts
  React.useEffect(() => {
    openNotification();
  }, [openNotification]);

  return null; // This component only handles notification logic
};

export default UploadCompleteNotification;
